import React, { useState, useEffect, useCallback, useMemo } from 'react';

import styles from './styles.module.scss';

// components
import Modal from '../../../../../../components/Modals/Modal';
import CredentialDetails from './Steps/CredentialDetails';
import DoorSettings from './Steps/DoorSettings';
import { FormControlLabel, IconButton, Radio, RadioGroup, Switch, withStyles } from '@material-ui/core';
import { doReadOrganisationSites, doUpdateOrganisationAccessPoint } from '../../../../../../redux/thunks/organisation';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../../../../components/Buttons/Button';
import { allCredTypes, credsTypesMap } from '../../../../../../utils/Common';
import { AZURE_ZKTECO_CONTROLLER_CONFIGS, ISTAR_CONTROLLER_CONFIGS, OTHERS_CONTROLLER_CONFIGS, SPINTLY_CONTROLLER_CONFIGS, WITHOUT_CONTROLLER_CONFIGS } from '../../../../../../constants/list/AccessPointTypeList';
import TextInput from '../../../../../../components/Inputs/TextInput';
import { CloseRounded } from '@material-ui/icons';
import Dropdown from '../../../../../../components/Inputs/Dropdown';
import FlexColumn from '../../../../../../components/FlexBox/FlexColumn';
import Typography from '../../../../../../components/Typography/Typography';
import SButton from '../../../../../../components/SButton/SButton';
import { colors } from '../../../../../../styles/colors';
import { ArrowRightIcon } from '@phosphor-icons/react';
import BoardMapping from './BoardMapping';
import SDialog from '../../../../../../components/SDialog/SDialog';
import useIsMobile from '../../../../../../hooks/useIsMobile';

const StyledRadio = withStyles({
    root: {
        color: '#BDC7D9',
        '&$checked': {
            color: '#03BFFF',
        },
    },
    checked: {},
})((props) => <Radio color="default" {...props} />);

const tabs = [
    { title: 'Details' },
    { title: 'Credential Types' },
    { title: 'Settings' },
]

const EditAccessPointModal = ({
    showEditAccessPointPopup,
    togglePopup,
    accessPoint,
    customerId,
    partnerId
}) => {

    const isMobile = useIsMobile();

    const popupToggle = useCallback(() => {
        togglePopup('EditAccessPoint');
    }, [togglePopup]);

    const dispatch = useDispatch();

    const sites = useSelector((state) => state.organisation.sites);

    const [accessPointDetails, setAccessPointDetails] = useState({});
    const [initialAccessPointDetails, setInitialAccessPointDetails] = useState({});
    const [success, setSuccess] = useState({ show: false, accessPoint: '' })
    const [activeStep, setActiveStep] = useState(0);
    const [showElevatorMapping, setElevatorMapping] = useState(false);
    const [inProgress, setInProgress] = useState(false);
    const [credentialChangeDialog, setCredentialChangeDialog] = useState(false);

    function diffObjects(original, updated) {
        const added = [];
        const deleted = [];
        const changed = [];

        const allKeys = new Set([...Object.keys(original), ...Object.keys(updated)]);

        for (const key of allKeys) {
            const prev = original[key];
            const curr = updated[key];
            const id = parseInt(key.replace("CH", ""), 10);

            if (prev && !curr) {
                deleted.push({
                    channelId: id,
                    operation: 2
                })
                continue;
            }

            if (!prev && curr) {
                added[key] = curr;
                added?.push({
                    channelId: id,
                    operation: 1,
                    applicationId: curr?.floor?.floorId,
                    applicationName: curr?.floor?.value
                })
                continue;
            }


            if (JSON.stringify(prev) !== JSON.stringify(curr)) {
                changed?.push({
                    channelId: id,
                    operation: 3,
                    applicationId: curr?.floor?.floorId,
                    applicationName: curr?.floor?.value
                })
            }
        }

        return [...added, ...deleted, ...changed];
    }

    useEffect(() => {
        const apDetails = {
            accessPointName: accessPoint?.name,
            withController: accessPoint?.isControllerAttached,
            controllerType: String(accessPoint?.controllerType),
            commonCredentials: String(accessPoint?.credentials?.commonCredentialsValue),
            commonCredentialBitSize: accessPoint?.credentials?.commonCredentialBitSize ? `${accessPoint?.credentials?.commonCredentialBitSize} bits` : "",
            cardNumber: accessPoint?.credentials?.cardNumber,
            facilityCode: accessPoint?.credentials?.facilityCode,
            comProtocal: String(accessPoint?.wireProtocol),
            attendanceEnabled: accessPoint?.enableAttendance,
            relayTime: accessPoint?.relaySettings?.relayOnTime,
            inveteredRelayLogic: accessPoint?.relaySettings?.invertRelayLogic,
            auxInput: {
                enabled: !!accessPoint?.devices[0]?.auxInputEnable,
                inputType: String(accessPoint?.devices[0]?.auxInputType || 0)
            },
            doorSensorSettings: {
                doorSensorEnable: accessPoint?.doorSensorSettings?.doorSensorEnable,
                doorSensorType: accessPoint?.doorSensorSettings?.doorSensorType
            },
            showCredentialTypes: accessPoint?.showCredentialTypes,
            showCommonCredentials: accessPoint?.showCommonCredentials,
            channelExtenderSettings: accessPoint?.channelExtenderSettings,
            frDevice: {
                enabled: false,
                entryDevice: {
                    serialNumber: '',
                    error: '',
                    description: ''
                },
                exitDevice: {
                    serialNumber: '',
                    error: '',
                    description: ''
                }
            }
        }

        let configs = [];
        if (!accessPoint?.isControllerAttached) {
            configs = [...WITHOUT_CONTROLLER_CONFIGS]
        } else if (accessPoint?.controllerType === 1) {
            configs = [...SPINTLY_CONTROLLER_CONFIGS]
        } else if (accessPoint?.controllerType === 4) {
            configs = [...OTHERS_CONTROLLER_CONFIGS]
        } else if (accessPoint?.controllerType === 2) {
            configs = [...AZURE_ZKTECO_CONTROLLER_CONFIGS]
        } else {
            configs = [...ISTAR_CONTROLLER_CONFIGS]
        }

        const selectedConfiguration = configs?.find(elem => elem?.value === accessPoint?.configuration);
        apDetails["configuration"] = selectedConfiguration;

        if (selectedConfiguration?.formOptions?.lockingMechanism) {
            apDetails["lockingMech"] = Object?.values(selectedConfiguration?.lockingMech)?.find(elem => elem?.value === accessPoint?.lockingMechanism)
        }

        if (apDetails?.channelExtenderSettings?.channelDetails) {
            apDetails["channelExtenderSettings"]["channelMapping"] = apDetails?.channelExtenderSettings?.channelDetails?.reduce((acc, item) => {
                acc[`CH${item?.channelId}`] = { checked: true }
                acc[`CH${item?.channelId}`]["floor"] = {
                    label: item?.applicationName,
                    value: item?.applicationName,
                    floorId: item?.applicationId,
                }
                return acc;
            }, {});
            apDetails["channelExtenderSettings"]["exisitingFloors"] = apDetails?.channelExtenderSettings?.channelDetails?.map(cd => {
                return {
                    floorId: cd?.applicationId,
                    label: cd?.applicationName,
                    value: cd?.applicationName,
                    selected: true,
                }
            })
        }

        if (accessPoint?.useFRDevice) {
            apDetails["frDevice"]["enabled"] = true;
            if (accessPoint?.frDevices?.length > 0) {
                for (let i = 0; i < accessPoint?.frDevices?.length; i++) {
                    if (accessPoint?.frDevices[i]["deviceType"] === 1) {
                        apDetails["frDevice"]["entryDevice"]["serialNumber"] = accessPoint?.frDevices[i]["serialNumber"];
                    }

                    if (accessPoint?.frDevices[i]["deviceType"] === 2) {
                        apDetails["frDevice"]["exitDevice"]["serialNumber"] = accessPoint?.frDevices[i]["serialNumber"];
                    }
                }
            }
        }


        if (accessPoint?.credentials) {
            apDetails["supportedAccessType"] = accessPoint?.credentials?.supportedAccessType;

            if (accessPoint?.credentials?.enabledAccessType) {
                let bits = Number(accessPoint?.credentials?.enabledAccessType)?.toString(2);
                if (bits?.length > 3) {
                    bits = bits.slice(0, -5) + bits.slice(-3)
                }
                const checkedCredTypes = { ...credsTypesMap }
                for (let i = 0; i < allCredTypes?.length; i++) {
                    if (bits[bits?.length - 1 - i] === "1") {
                        checkedCredTypes[allCredTypes[i]?.id] = true
                    } else {
                        checkedCredTypes[allCredTypes[i]?.id] = false
                    }
                }
                apDetails["checkedCredTypes"] = checkedCredTypes;

            }
            if (accessPoint?.credentials?.commonCredentials) {
                let bits = Number(accessPoint?.credentials?.commonCredentials)?.toString(2);
                if (bits?.length > 3) {
                    bits = bits.slice(0, -5) + bits.slice(-3)
                }
                const checkedCommonCred = { ...credsTypesMap, count: 0 }
                for (let i = 0; i < allCredTypes?.length; i++) {
                    if (bits[bits?.length - 1 - i] === "1") {
                        checkedCommonCred[allCredTypes[i]?.id] = true
                        checkedCommonCred["count"] = checkedCommonCred?.count + 1
                    } else {
                        checkedCommonCred[allCredTypes[i]?.id] = false
                    }
                }
                apDetails["checkedCommonCred"] = checkedCommonCred;
            }
        }
        setAccessPointDetails(JSON.parse(JSON.stringify(apDetails)));
        setInitialAccessPointDetails(JSON.parse(JSON.stringify(apDetails)))
    }, [accessPoint])


    function bitMapCreds(data) {
        const keys = Object?.keys(credsTypesMap);

        const bitMap = [];

        keys.forEach((key, index) => {
            if (data?.[key]) {
                if (key === "mobile_ble") {
                    bitMap.push(1, 1, 1);
                } else {
                    bitMap.push(1);
                }
            } else {
                if (key === "mobile_ble") {
                    bitMap.push(0, 0, 0);
                } else {
                    bitMap.push(0);
                }
            }
        });

        return parseInt(bitMap?.reverse()?.join(''), 2);
    }

    const generateAccessPointsPayload = useCallback((accessPointData) => {
        const apData = {}

        if (accessPointData?.accessPointName !== accessPoint?.name) {
            apData["name"] = accessPointData?.accessPointName
        }
        if (accessPointData?.auxInput?.enabled !== accessPoint?.devices[0]?.auxInputEnable ||
            Number(accessPointData?.auxInput?.inputType) !== accessPoint?.devices[0]?.auxInputType
        ) {
            apData["aux"] = {
                auxInputEnable: accessPointData?.auxInput?.enabled,
                auxInputType: accessPointData?.auxInput?.enabled ? Number(accessPointData?.auxInput?.inputType) : 0,
            };
        }

        if (accessPointData?.attendanceEnabled !== accessPoint?.enableAttendance) {
            apData["enableAttendance"] = accessPointData?.attendanceEnabled
        }

        if (accessPointData?.withController &&
            Number(accessPointData?.comProtocal) !== accessPoint?.wireProtocol
        ) {
            apData["wireProtocol"] = Number(accessPointData?.comProtocal)
        }

        if (accessPointData?.doorSensorSettings?.doorSensorEnable !== accessPoint?.doorSensorSettings?.doorSensorEnable ||
            accessPointData?.doorSensorSettings?.doorSensorType !== accessPoint?.doorSensorSettings?.doorSensorType
        ) {
            apData['doorSensorSettings'] = {
                doorSensorEnable: accessPointData?.doorSensorSettings?.doorSensorEnable,
                doorSensorType: accessPointData?.doorSensorSettings?.doorSensorType
            };
        }

        if (accessPointData?.inveteredRelayLogic !== accessPoint?.relaySettings?.invertRelayLogic ||
            accessPointData?.relayTime !== accessPoint?.relaySettings?.relayOnTime
        ) {
            apData['relaySettings'] = {
                relayOnTime: Number(accessPointData?.relayTime),
                invertRelayLogic: accessPointData?.inveteredRelayLogic
            };
        }

        if ((accessPointData?.configuration?.value !== 4) &&
            (bitMapCreds(accessPointData?.checkedCredTypes) !== accessPoint?.credentials?.enabledAccessType ||
                bitMapCreds(accessPointData?.checkedCommonCred) !== accessPoint?.credentials?.commonCredentials ||
                accessPointData?.commonCredentials !== String(accessPoint?.credentials?.commonCredentialsValue))
        ) {
            apData["credentials"] = {
                enabledAccessType: bitMapCreds(accessPointData?.checkedCredTypes)
            }
            if (accessPointData?.controllerType === "4") {
                apData["credentials"]["commonCredentials"] = bitMapCreds(accessPointData?.checkedCommonCred);
                apData["credentials"]["commonCredentialsBitSize"] = Number(accessPointData?.commonCredentialFormat?.slice(0, 2)) || 0;

                if (apData?.credentials?.commonCredentials === 0) {
                    apData["credentials"]["changeForAllAccessPoints"] = false
                } else {
                    apData["credentials"]["changeForAllAccessPoints"] = true
                }

                if (accessPointData?.commonCredentialType === "1") {
                    apData["credentials"]["commonCredentialsValue"] = Number(accessPointData?.commonCredentials?.value) || 0;
                } else {
                    apData["credentials"]["cardNumber"] = Number(accessPointData?.cardNumber?.value);
                    apData["credentials"]["facilityCode"] = Number(accessPointData?.facilityCode?.value);
                }
            }
        }

        if (accessPointData?.configuration?.value === 9) {
            const filtered = Object.fromEntries(
                Object.entries(accessPointData?.channelExtenderSettings?.channelMapping).filter(([key, value]) => value.checked)
            );
            const updatedMappings = diffObjects(accessPoint?.channelExtenderSettings?.channelMapping || {}, filtered || {});
            if (updatedMappings?.length > 0) {
                apData["channelExtenderSettings"] = {
                    channelDetails: updatedMappings
                }
            }
        }

        if (accessPointData?.frDevice?.enabled) {
            apData["useFRDevice"] = true
            apData["frDevices"] = []
            if (accessPointData?.frDevice?.entryDevice?.serialNumber) {
                apData["frDevices"].push({
                    serialNumber: accessPointData?.frDevice?.entryDevice?.serialNumber,
                    deviceType: 1
                })
            }

            if (accessPointData?.frDevice?.exitDevice?.serialNumber) {
                apData["frDevices"].push({
                    serialNumber: accessPointData?.frDevice?.exitDevice?.serialNumber,
                    deviceType: 2
                })
            }
        }
        return apData;
    }, [accessPoint?.credentials?.commonCredentials,
    accessPoint?.credentials?.commonCredentialsValue,
    accessPoint?.credentials?.enabledAccessType,
    accessPoint?.devices,
    accessPoint?.name,
    accessPoint?.relaySettings?.invertRelayLogic,
    accessPoint?.relaySettings?.relayOnTime,
    accessPoint?.wireProtocol,
    accessPoint?.doorSensorSettings,
    accessPoint?.enableAttendance
    ])

    const handleSave = useCallback(() => {
        const postData = generateAccessPointsPayload(accessPointDetails);
        if (Object?.keys(postData)?.length > 0) {
            setInProgress(true);
            setSuccess({ show: false, accessPoint: accessPointDetails?.accessPointName })
            dispatch(doUpdateOrganisationAccessPoint({
                partnerId,
                organisationId: customerId,
                accessPointId: accessPoint?.id,
                payload: postData,
                callback: () => {
                    if (postData?.credentials?.changeForAllAccessPoints) {
                        dispatch(
                            doReadOrganisationSites({
                                partnerId: partnerId,
                                organisationId: customerId,
                                displayNotification: false,
                            }),
                        );
                    }
                    setSuccess(prev => ({ ...prev, show: true }))
                },
                onFinally: () => {
                    setInProgress(false);
                }
            }))
        }
    }, [accessPointDetails, customerId, dispatch, generateAccessPointsPayload, partnerId, accessPoint?.id])

    const isDisabled = useMemo(() => {
        if (activeStep === 1 &&
            accessPointDetails?.controllerType === "4" &&
            accessPointDetails?.checkedCommonCred?.count > 0
        ) {
            if (accessPointDetails?.commonCredentialType === "1" && (accessPointDetails?.commonCredentials?.value?.trim() === '' || accessPointDetails?.commonCredentials?.error)) {
                return true;
            }

            if (accessPointDetails?.commonCredentialType === "2" && (accessPointDetails?.facilityCode?.value?.trim() === '' || accessPointDetails?.facilityCode?.error || accessPointDetails?.cardNumber?.value?.trim() === '' || accessPointDetails?.cardNumber?.error)) {
                return true;
            }
        }

        if ((activeStep === 2 || accessPointDetails?.configuration?.value === 4)) {
            const postData = generateAccessPointsPayload(accessPointDetails);
            if (Object?.keys(postData)?.length > 0) {
                return false
            } else {
                return true;
            }
        }
        return false;
    }, [accessPointDetails, activeStep])

    const handleUpdate = useCallback((data) => {
        setAccessPointDetails(prev => ({ ...prev, ...data }))
    }, [])

    const handleFRSerial = (value, field) => {
        const prodId = value.substring(0, 4);
        let description = '';
        let error = ''
        if (value?.length !== 14) {
            error = 'Please enter a valid Serial Number'
        }
        if (prodId === '102D') {
            description = 'Face'
        } else {
            error = "Serial number doesn't support the configuration";
        }

        setAccessPointDetails(prev => ({
            ...prev,
            frDevice: {
                ...prev.frDevice,
                [field]: {
                    serialNumber: value,
                    error,
                    description
                }
            }
        }))
    }

    const renderStepper = (steps = [], currentStep = 0) => {
        return (<>
            {steps?.length > 1 && <div className={styles['page-flag-container']}>
                {steps?.map((step, index) => {
                    return (
                        <>
                            <div className={index <= currentStep ? styles['page-flag'] : styles['page-flag-next']}>{index + 1}</div>
                            {
                                (index + 1) !== steps?.length &&
                                <div className={styles['page-flag-separator-next']}></div>
                            }
                        </>
                    )
                })}
            </div>}
            {steps[currentStep]?.title &&
                <div className={styles['form-name-container']}>
                    <h2 className={styles['form-name']}>{steps[currentStep]?.title}</h2>
                </div>
            }
        </>)
    }

    const hDisableNext = useMemo(() => {
        if (activeStep === 0) {
            if (accessPointDetails?.frDevice?.enabled) {
                if (accessPointDetails?.configuration?.formOptions?.entry) {
                    if (accessPointDetails?.frDevice?.entryDevice?.error || !accessPointDetails?.frDevice?.entryDevice?.serialNumber) {
                        return true;
                    }
                }
                if (accessPointDetails?.configuration?.formOptions?.exit) {
                    if (accessPointDetails?.frDevice?.exitDevice?.error || !accessPointDetails?.frDevice?.exitDevice?.serialNumber) {
                        return true;
                    }
                }
            }
        }

        return false;
    }, [activeStep, accessPointDetails]);

    const commonCredentialChanged = () => {
        const siteCreds = sites?.find(s => s?.id === accessPoint?.siteId);
        if (!siteCreds?.commonCredentailEnable || accessPointDetails?.checkedCommonCred?.count <= 0) {
            return false;
        }

        if (siteCreds?.commonCredentialsBitSize !== Number(accessPointDetails?.commonCredentialFormat?.slice(0, 2))) {
            return true;
        }

        if (accessPointDetails?.commonCredentialType === "1" && Number(siteCreds?.commonCredentialsValue) !== Number(accessPointDetails?.commonCredentials?.value)) {
            return true;
        }

        if (accessPointDetails?.commonCredentialType === "2" && (Number(siteCreds?.facilityCode) !== Number(accessPointDetails?.facilityCode?.value) || Number(siteCreds?.cardNumber) !== Number(accessPointDetails?.cardNumber?.value))) {
            return true;
        }

        return false;
    }

    return (
        <>
            <SDialog
                title='Edit Access Point'
                open={showEditAccessPointPopup && !success?.show}
                onClose={popupToggle}
                primaryButtonConfig={{
                    text: (activeStep === 2 || accessPointDetails?.configuration?.value === 4 || (activeStep === 1 && accessPointDetails?.controllerType === "4")) ? "Save" : 'Next',
                    disabled: inProgress || ((activeStep === 2 || accessPointDetails?.configuration?.value === 4 || (activeStep === 1 && accessPointDetails?.controllerType === "4")) ? isDisabled : hDisableNext),
                    onClick: () => {
                        if (activeStep === 2 || accessPointDetails?.configuration?.value === 4) {
                            handleSave()
                        } else if (activeStep === 1 && accessPointDetails?.controllerType === "4") {
                            const selectedSite = sites?.find(s => s?.id === accessPoint?.siteId);
                            if (commonCredentialChanged()) {
                                setCredentialChangeDialog(true);
                            } else {
                                handleSave();
                            }
                        } else {
                            setActiveStep(activeStep + 1);
                        }
                    }
                }}
                secondaryButtonConfig={activeStep > 0 ? {
                    text: 'Back',
                    disabled: inProgress,
                    onClick: () => { setActiveStep(activeStep - 1) }
                } : {}}
                isFormDialog={true}
            >
                <FlexColumn alignItems='start'>
                    {renderStepper(accessPointDetails?.configuration?.value === 4 ? [tabs[0]] : accessPointDetails?.controllerType === "4" ? tabs?.slice(0, 2) : tabs, activeStep)}

                    {
                        activeStep === 0 ?
                            <div className='flex' style={{ marginTop: '1rem', flexDirection: 'column', gap: '1rem', width: '100%' }}>
                                <TextInput
                                    name=""
                                    placeholder="Access Point Name"
                                    value={accessPointDetails?.accessPointName}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setAccessPointDetails(prev => ({ ...prev, accessPointName: value?.trimStart() }))
                                    }}
                                />
                                {accessPointDetails?.configuration?.formOptions?.entry && <TextInput
                                    name=""
                                    disabled={true}
                                    placeholder="Entry Reader Serial Number"
                                    value={accessPoint?.devices?.find(d => d?.type === 1)?.serialNumber}
                                    onChange={(e) => { }}
                                />}
                                {accessPointDetails?.configuration?.formOptions?.exit && <TextInput
                                    name=""
                                    disabled={true}
                                    placeholder="Exit Reader Serial Number"
                                    value={accessPoint?.devices?.find(d => d?.type === 2)?.serialNumber}
                                    onChange={(e) => { }}
                                />}

                                {accessPointDetails?.configuration?.value === 9 &&
                                    <FlexColumn>
                                        <Dropdown
                                            placeholder="Application"
                                            value={accessPointDetails?.channelExtenderSettings?.application === 1 ? "Elevator" : "Locker"}
                                            onChange={(data) => {

                                            }}
                                            disabled={true}
                                            data={["Elevator", "Locker"]}
                                        />
                                        <FlexColumn alignItems="start">
                                            <Dropdown
                                                placeholder="No of boards"
                                                disabled={true}
                                                value={accessPointDetails?.channelExtenderSettings?.numberOfBoards}
                                                onChange={(data) => {
                                                    setAccessPointDetails(prev => ({
                                                        ...prev,
                                                        channelExtenderSettings: {
                                                            ...prev?.channelExtenderSettings,
                                                            numberOfBoards: data
                                                        }
                                                    }));
                                                }}
                                                data={[1, 2, 3, 4]}
                                            />
                                            <Typography variant={"b3R"} color={colors.grey500}>The reader and the boards are linked in a series configuration. The board that connects to the reader is the first board in the series, while the board connected to the first board is the second board, and this pattern continues onward.</Typography>

                                            <SButton disabled={!accessPointDetails?.channelExtenderSettings?.numberOfBoards} variant={"secondary"} color={"info"} onClick={() => { setElevatorMapping(true) }}>Board and {accessPointDetails?.channelExtenderSettings?.application === 1 ? "Elevator" : "Locker"} Mapping <ArrowRightIcon style={{ marginLeft: '0.5rem' }} size={16} /></SButton>
                                        </FlexColumn>
                                    </FlexColumn>
                                }

                                {(accessPointDetails?.configuration?.formOptions?.entry || accessPointDetails?.configuration?.formOptions?.exit) && <div className={styles['column']}>
                                    <div className={styles['row']}>
                                        <div className={styles['column']}>
                                            <h2 className={styles['bold-text']}>Use FR device</h2>
                                            <p className={styles['note']}>Enable to add a Face Recognition device to the access point</p>
                                        </div>
                                        <Switch
                                            disabled={accessPoint?.useFRDevice}
                                            checked={accessPointDetails?.frDevice?.enabled}
                                            onChange={() => { setAccessPointDetails(prev => ({ ...prev, frDevice: { ...prev.frDevice, enabled: !prev?.frDevice?.enabled } })) }}
                                        />
                                    </div>
                                    <div className={styles.border} />
                                </div>}

                                {accessPointDetails?.frDevice?.enabled && <div className={"flex"} style={{ flexDirection: 'column', gap: '1rem' }}>
                                    <TextInput
                                        name=""
                                        placeholder="FR Entry Serial Number"
                                        disabled={accessPoint?.useFRDevice}
                                        value={accessPointDetails?.frDevice?.entryDevice?.serialNumber}
                                        error={accessPointDetails?.frDevice?.entryDevice?.error}
                                        description={accessPointDetails?.frDevice?.entryDevice?.description}
                                        maxLength={14}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            handleFRSerial(value?.trim(), "entryDevice")
                                        }}
                                    />
                                    {accessPointDetails?.configuration?.formOptions?.exit && <TextInput
                                        name=""
                                        placeholder="FR Exit Serial Number"
                                        disabled={accessPoint?.useFRDevice}
                                        value={accessPointDetails?.frDevice?.exitDevice?.serialNumber}
                                        error={accessPointDetails?.frDevice?.exitDevice?.error}
                                        description={accessPointDetails?.frDevice?.exitDevice?.description}
                                        maxLength={14}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            handleFRSerial(value?.trim(), "exitDevice")
                                        }}
                                    />}
                                </div>}
                                <div style={{ marginBottom: '2rem' }}>

                                    {/* DO NOT REMOVE REQUIRED IN NEXT PHASE */}
                                    {/* {accessPointDetails?.withController && accessPointDetails?.controllerType === "1" && */}
                                    {false &&
                                        <>
                                            <div className={styles['column']} style={{ marginTop: '2rem' }}>
                                                <div className={styles['row']}>
                                                    <div className={styles['column']}>
                                                        <h2 className={styles['bold-text']}>Aux Input</h2>
                                                        <p className={styles['note']}>Connect a device on the Weigand port</p>
                                                    </div>
                                                    <Switch
                                                        checked={accessPointDetails?.auxInput?.enabled}
                                                        onChange={() => { setAccessPointDetails(prev => ({ ...prev, auxInput: { ...prev.auxInput, enabled: !prev?.auxInput?.enabled } })) }}
                                                    />
                                                </div>
                                                {accessPointDetails?.auxInput?.enabled && <RadioGroup aria-label="AUX-Input-Type" name="auxInputType" value={accessPointDetails?.auxInput?.inputType} onChange={(e) => { setAccessPointDetails(prev => ({ ...prev, auxInput: { ...prev.auxInput, inputType: e?.target?.value } })) }}>
                                                    <FormControlLabel value="3" control={<StyledRadio />} label="Others" />
                                                    <FormControlLabel value="1" control={<StyledRadio />} label="Keypad" />
                                                    <FormControlLabel value="2" control={<StyledRadio />} label="UHF Reader" />
                                                </RadioGroup>}
                                            </div>
                                        </>
                                    }

                                </div>
                            </div>
                            :
                            activeStep === 1 ?
                                (accessPointDetails?.configuration?.value !== 4 && <CredentialDetails renderTitle={false} accessPointDetails={initialAccessPointDetails} mode={1} hUpdate={handleUpdate} />)
                                :
                                activeStep === 2 ?
                                    (accessPointDetails?.controllerType !== "4" &&
                                        <>
                                            <DoorSettings accessPointDetails={initialAccessPointDetails} mode={1} hUpdate={handleUpdate} />
                                        </>
                                    ) :
                                    <></>
                    }
                </FlexColumn>
            </SDialog>

            <SDialog
                title='Access Point Updated'
                open={success?.show}
                onClose={() => {
                    popupToggle()
                    setSuccess(prev => ({ ...prev, show: false }));
                }}
                titleConfig={{
                    variant: 'h6M'
                }}
                primaryButtonConfig={{
                    text: 'Done',
                    onClick: () => {
                        popupToggle()
                        setSuccess(prev => ({ ...prev, show: false }));
                    },
                    fullWidth: false
                }}
                actionsContainer={{
                    justifyContent: 'end'
                }}
            >
                <Typography variant={isMobile ? "b2M" : "b1M"} style={{ padding: '1.5rem 0' }}>Successfully updated access point "{success?.accessPoint}"</Typography>
            </SDialog>


            {showElevatorMapping && <BoardMapping
                application={accessPointDetails?.channelExtenderSettings?.application === 1 ? "Elevator" : "Locker"}
                noOfBoards={accessPointDetails?.channelExtenderSettings?.numberOfBoards}
                prevChannelMapping={accessPointDetails?.channelExtenderSettings?.channelMapping}
                prevFloors={accessPointDetails?.channelExtenderSettings?.exisitingFloors}
                onBack={() => { setElevatorMapping(false) }}
                onSave={(cMapping, floors) => {
                    setAccessPointDetails(prev => ({
                        ...prev,
                        channelExtenderSettings: {
                            ...prev?.channelExtenderSettings,
                            channelMapping: cMapping,
                            exisitingFloors: floors
                        }
                    }))

                    setElevatorMapping(false)
                }}
            />}

            <SDialog
                open={credentialChangeDialog}
                onClose={() => { setCredentialChangeDialog(false); }}
                title="Common credentials changed"
                primaryButtonConfig={{
                    text: 'Okay',
                    onClick: () => {
                        handleSave();
                        setCredentialChangeDialog(false);
                    }
                }}
            >
                <Typography variant="b1SB" style={{ color: colors.error500 }}>Common credentials have been changed. This will change the credential values for all existing device and at organisation level</Typography>
            </SDialog>

        </>
    );
};

export default EditAccessPointModal;
